import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import {
    createAssetsDiscountsEntryByInstallmentAction,
    modifyAssetsDiscountsEntryByInstallmentAction,
} from "../../actions";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import useHandleForm from "./useHandleForm";
import { TextField, Grid, Select, MenuItem, FormControl, InputLabel } from "@material-ui/core";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { dateLocale } from "@icarius-localization/strings";
import EditableGridv2 from "@icarius-common/editableGrid/editableGridv2";
import NumberFormatCustom from "@icarius-common/dynamicInputs/customNumberFormat";
import { addMonths } from "date-fns";
import { formatDateYYYYMM } from "@icarius-utils/date";
import { openDialogAction } from "@icarius-common/dialog/actions";
import ConfirmPrepaymentDialog from "./confirmPrepaymentDialog";
import useAutomaticProcesses from "@icarius-common/automaticProcesses/useAutomaticProcesses";
import CustomIconButton from "@icarius-common/abmComponents/customIconButton";
import ResultGridDialog from "@icarius-common/automaticProcesses/components/resultGridDialog";
import PickTemplateDialog from "@icarius-pages/employeesProcesses/components/dialogs/pickTemplateDialog";
import { VisibilityIcon } from "@icarius-icons/";
import SelectProcessDialog from "@icarius-common/automaticProcesses/components/selectProcessDialog";
import { getCountry } from "@icarius-app/selectors";
import paths from "@icarius-router/paths";
import CustomNumberFormat from "@icarius-common/dynamicInputs/customNumberFormat";

const gridStyle = { height: 75, padding: "0px 10px" };

const Form = (props) => {
    let { data } = props;
    const { isLoading, handleClose, selects, entryStructures, selectedEntryStructureCode, colaborators } = props;
    const dispatch = useDispatch();
    const [gridReference, setGridReference] = useState(null);

    const gridRef = useRef();

    useEffect(() => {
        if (gridRef) {
            setGridReference(gridRef.current);
        }
    }, [gridRef]);

    const {
        isExecution,
        automaticProcessesIsLoading,
        automaticProcessesIsEnabled,
        processList,
        resultData,
        resultDialogIsOpen,
        pickTemplateDialogIsOpen,
        processSelectionDialogIsOpen,
        executeProcess,
        handleExecute,
        viewResult,
        handleViewResult,
        handleCloseProcessDialogSelection,
        handleCloseResultDialog,
        handleOpenPickTemplateDialog,
        handleClosePickTemplateDialog,
        handleGenerateDocumentsGivenTemplate,
    } = useAutomaticProcesses(paths.calculateProcessAssetsDiscountsButton);

    const country = useSelector(getCountry);

    const create = (dataToSend) => {
        dispatch(createAssetsDiscountsEntryByInstallmentAction(dataToSend)).then(
            (res) => res?.status === 200 && handleClose()
        );
    };

    const modify = (dataToSend) => {
        dispatch(modifyAssetsDiscountsEntryByInstallmentAction(dataToSend)).then(
            (res) => res?.status === 200 && handleClose()
        );
    };

    const openValidationError = () => {
        dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
    };

    const getEntryStructure = useCallback(() => {
        const entryStructure = entryStructures.find((entry) => entry.code === selectedEntryStructureCode);
        return entryStructure || { periodicity: "", thirdPartyPayment: "" };
    }, [entryStructures, selectedEntryStructureCode]);

    const {
        isCreate,
        formData,
        setFormValue,
        submit,
        setCalculatedFieldsValues,
        setPaymenthMethodValue,
        setFormValueLoanDatePeriod,
        setConcept,
    } = useHandleForm(
        data,
        create,
        modify,
        selectedEntryStructureCode,
        openValidationError,
        gridRef,
        Boolean(getEntryStructure().thirdPartyPayment)
    );

    const [shouldReloadGrid, setShouldReloadGrid] = useState(false);
    const [confirmPrepaymentDialogIsOpen, setConfirmPrepaymentDialogIsOpen] = useState(false);
    const [gridData, setGridData] = useState([]);
    const [unpaidInstallmentsDialog, setUnpaidInstallmentsDialog] = useState([]);
    const [hasStarted, setHasStarted] = useState(false);

    const getColaboratorData = (employeeCode) => {
        return colaborators.find((colaborator) => colaborator["CODIGO DE EMPLEADO"] === employeeCode);
    };

    const selectDataSources = (name, data) => {
        switch (name) {
            case "Estado":
                return [
                    { key: "C", value: "Pagada" },
                    { key: "P", value: "Pendiente" },
                ];
            case "Concepto condicional 1":
            case "Concepto condicional 2":
            case "Concepto condicional 3":
                return selects.concepts;
            case getLocalizedString("conditionalInstallment"):
                return selects.installmentsConditions;
            default:
                return [];
        }
    };

    const showConfirmPostponeDialog = () => {
        dispatch(
            openDialogAction({
                msg: getLocalizedString("postponeLoanInstallmentsConfirmation"),
                title: getLocalizedString("confirmation"),
                onConfirm: postponeInstallments,
                acceptOnly: false,
            })
        );
    };

    const calculateGridData = useCallback(
        (gridRef) => {
            if (isCreate) {
                if (
                    formData["Concepto"] &&
                    parseFloat(formData["Total del préstamo"]) > 0 &&
                    parseInt(formData["Cantidad de cuotas"]) > 0 &&
                    (getEntryStructure().periodicity === "CP" ||
                        (getEntryStructure().periodicity === "C" && formData["Período de inicio del descuento"])) &&
                    parseInt(formData["Número de cuota de referencia"]) > 0
                ) {
                    // Limpio la grilla e inserto estos
                    const loanTotal = parseFloat(formData["Total del préstamo"]);

                    const installments = parseInt(formData["Cantidad de cuotas"]);

                    if (gridReference) {
                        const dataSource = gridReference.getDataSource();

                        dataSource
                            .store()
                            .totalCount()
                            .then((resp) => {
                                const installmentsInTable = resp;
                                let filter = gridReference.getCombinedFilter();

                                dataSource
                                    .store()
                                    .load({ sort: dataSource.sort(), filter: filter ? filter : null })
                                    .done((allData) => {
                                        const paidInstallmentsValue =
                                            allData.reduce(function(acc, obj) {
                                                const val = obj["Valor cuota"];
                                                const parsedVal =
                                                    typeof val === "string"
                                                        ? parseFloat(val.replace(/\./g, "").replace(/,/g, "."))
                                                        : parseFloat(val);
                                                return acc + (parsedVal || 0);
                                            }, 0) || 0;

                                        if (installments > 0) {
                                            let referenceSubstring = "";

                                            if (allData.length > 0) {
                                                referenceSubstring = allData[0]["Referencia"];
                                                referenceSubstring = referenceSubstring
                                                    .substring(0, referenceSubstring.lastIndexOf("Cuota"))
                                                    .trim();
                                            }

                                            if (
                                                (allData.length > 0 &&
                                                    loanTotal.toFixed(2) !== paidInstallmentsValue.toFixed(2)) ||
                                                installmentsInTable !== installments ||
                                                formData["Nombre del concepto"] !== referenceSubstring
                                            ) {
                                                setCalculatedFieldsValues(["0", loanTotal, "0", installments]);

                                                const loanPerInstallment = loanTotal / installments;
                                                const installmentReference =
                                                    parseInt(formData["Número de cuota de referencia"]) || 1;

                                                // Compute a single consistent value for all regular installments.
                                                // CL uses integer amounts (floor), others keep 3 decimal places.
                                                // The last installment is always the true remainder so the total is exact.
                                                const regularInstallment =
                                                    country === "CL"
                                                        ? Math.floor(loanPerInstallment)
                                                        : parseFloat(loanPerInstallment.toFixed(3));

                                                let rows = [];

                                                for (let i = 0; i < installments; i++) {
                                                    let rowData = {};
                                                    rowData["Cuota"] = i + 1;

                                                    if (i === installments - 1) {
                                                        // Last installment absorbs the remainder so sum === loanTotal
                                                        const remainder =
                                                            loanTotal - regularInstallment * (installments - 1);
                                                        rowData["Valor cuota"] =
                                                            country === "CL"
                                                                ? Math.round(remainder)
                                                                : parseFloat(remainder.toFixed(3));
                                                    } else {
                                                        rowData["Valor cuota"] = regularInstallment;
                                                    }

                                                    rowData["Estado"] = "P";
                                                    rowData["Referencia"] = `${
                                                        formData["Nombre del concepto"]
                                                    } ${getLocalizedString("installment")} ${installmentReference +
                                                        i}/${installmentReference + installments - 1}`;

                                                    if (getEntryStructure().periodicity === "C") {
                                                        rowData["Período de descuento"] = formatDateYYYYMM(
                                                            addMonths(
                                                                new Date(formData["Período de inicio del descuento"]),
                                                                i
                                                            )
                                                        );
                                                    } else {
                                                        rowData["Concepto condicional 1"] = null;
                                                        rowData["Concepto condicional 2"] = null;
                                                        rowData["Concepto condicional 3"] = null;
                                                        rowData["Condicional cuota"] = null;
                                                    }

                                                    rows.push(rowData);
                                                }
                                                setGridData(rows);
                                            }
                                        }
                                    });
                            });
                    }
                }
            } else {
                let rowData = [];

                if (data && data.detail && data.detail.length > 0) {
                    rowData = data.detail.map((originalRow) => {
                        let el = { ...originalRow };

                        if (el["Valor cuota"] !== undefined && el["Valor cuota"] !== null) {
                            el["Valor cuota"] = parseFloat(el["Valor cuota"]);
                        }

                        return el;
                    });
                }
                setGridData(rowData);
            }
        },
        [data, formData, getEntryStructure, isCreate, setCalculatedFieldsValues, gridReference, country]
    );

    useEffect(() => {
        setShouldReloadGrid(true);
    }, [gridData]);

    const getRows = useCallback(async () => {
        return new Promise(function(resolve, reject) {
            if (gridReference) {
                const dataSource = gridReference.getDataSource();
                dataSource
                    .store()
                    .totalCount()
                    .then((resp) => {
                        let filter = gridReference.getCombinedFilter();
                        dataSource
                            .store()
                            .load({ sort: dataSource.sort(), filter: filter ? filter : null })
                            .done((allData) => {
                                return resolve(allData);
                            });
                    });
            }
        });
    }, [gridReference]);

    useEffect(() => {
        if (!hasStarted && !isCreate && data && data.detail && gridReference) {
            getRows(gridReference).then((rows) => {
                if (rows.length === 0) {
                    calculateGridData(gridReference);
                    setHasStarted(true);
                }
            });
        }
    }, [hasStarted, calculateGridData, isCreate, data, gridReference, getRows]);

    const openPrePaymentDialog = () => {
        getUnpaidInstallments().then((rows) => {
            setUnpaidInstallmentsDialog(rows);
            if (rows) {
                setConfirmPrepaymentDialogIsOpen(true);
            }
        });
    };
    const prepayInstallments = async (quantity) => {
        const dataSource = gridReference.getDataSource();
        getRows(gridReference).then((rows) => {
            getUnpaidInstallments().then((pendingInstallments) => {
                if (quantity <= pendingInstallments.length) {
                    for (let i = 0; i < quantity; i++) {
                        const index = pendingInstallments.length - i - 1;
                        if (dataSource) {
                            dataSource
                                .store()
                                .update(pendingInstallments[index], {
                                    ...pendingInstallments[index],
                                    Estado: "C",
                                    Referencia: `${pendingInstallments[index]["Referencia"]} (${getLocalizedString(
                                        "prepaid"
                                    )})`,
                                })
                                .then(function() {
                                    dataSource.reload();
                                });
                        }
                    }

                    const pendingInstallmentsValue =
                        rows.reduce(function(acc, obj) {
                            const valP = obj["Valor cuota"];
                            const parsedValP =
                                typeof valP === "string"
                                    ? parseFloat(valP.replace(/\./g, "").replace(/,/g, "."))
                                    : parseFloat(valP);
                            return obj["Estado"] === "P" ? acc + (parsedValP || 0) : acc;
                        }, 0) || 0;
                    const paidInstallmentsValue =
                        rows.reduce(function(acc, obj) {
                            const valC = obj["Valor cuota"];
                            const parsedValC =
                                typeof valC === "string"
                                    ? parseFloat(valC.replace(/\./g, "").replace(/,/g, "."))
                                    : parseFloat(valC);
                            return obj["Estado"] === "C" ? acc + (parsedValC || 0) : acc;
                        }, 0) || 0;

                    const pendingInstallmentsQuantity =
                        rows.reduce(function(acc, obj) {
                            return obj["Estado"] === "P" ? acc + 1 : acc;
                        }, 0) || 0;

                    const paidInstallmentsQuantity =
                        rows.reduce(function(acc, obj) {
                            return obj["Estado"] === "C" ? acc + 1 : acc;
                        }, 0) || 0;

                    setCalculatedFieldsValues([
                        paidInstallmentsValue,
                        pendingInstallmentsValue,
                        paidInstallmentsQuantity,
                        pendingInstallmentsQuantity,
                    ]);
                }

                setConfirmPrepaymentDialogIsOpen(false);
            });
        });
    };

    const modifyDataInDataGrid = async (e, name) => {
        const dataSource = gridReference.getDataSource();
        getRows(gridReference).then((installments) => {
            const value = e.length > 0 ? e.replace(/-/g, "") : "";

            if (name === "Período de inicio del descuento") {
                for (let i = 0; i < installments.length; i++) {
                    if (dataSource) {
                        dataSource
                            .store()
                            .update(installments[i], {
                                ...installments[i],
                                "Período de descuento": formatDateYYYYMM(addMonths(new Date(e), i)),
                            })
                            .then(function() {
                                dataSource.reload();
                            });
                    }
                }
            } else if (name === "Número de cuota de referencia") {
                const installmentReference = parseInt(value) || 1;
                for (let i = 0; i < installments.length; i++) {
                    if (dataSource) {
                        dataSource
                            .store()
                            .update(installments[i], {
                                ...installments[i],
                                Referencia: `${formData["Nombre del concepto"]} ${getLocalizedString(
                                    "installment"
                                )} ${installmentReference + i}/${installmentReference + installments.length - 1}`,
                            })
                            .then(function() {
                                dataSource.reload();
                            });
                    }
                }
            }

            setFormValue(e, name);
        });
    };

    const getUnpaidInstallments = async () => {
        return new Promise(function(resolve, reject) {
            if (gridReference) {
                getRows(gridReference).then((rows) => {
                    resolve(rows.filter((row) => row["Estado"] === "P"));
                });
            }
        });
    };

    const setLoanDate = (e) => {
        const loanPeriod = formatDateYYYYMM(new Date(e));
        setFormValueLoanDatePeriod(e, loanPeriod);
    };

    const postponeInstallments = () => {
        const dataSource = gridReference.getDataSource();
        getUnpaidInstallments().then((pendingInstallments) => {
            for (let i = 0; i < pendingInstallments.length; i++) {
                if (dataSource) {
                    dataSource
                        .store()
                        .update(pendingInstallments[i], {
                            ...pendingInstallments[i],
                            "Período de descuento": formatDateYYYYMM(
                                addMonths(new Date(pendingInstallments[i]["Período de descuento"]), 1)
                            ),
                        })
                        .then(function() {
                            dataSource.reload();
                        });
                }
            }
        });
    };

    const columnsData = useMemo(() => {
        let columns = [];

        // Aca arranco a poner lo que hace falta
        const entryStructure = getEntryStructure();

        columns.push({
            headerName: getLocalizedString("installment"),
            field: "Cuota",
            isObligatory: true,
            maxLength: 254,
            minLength: 0,
            isNumeric: true,
            validationUnrequired: true,
            disabled: true,
        });
        columns.push({
            headerName: getLocalizedString("state"),
            field: "Estado",
            isObligatory: true,
            isSelect: true,
            disabled: true,
        });

        columns.push({
            field: "Valor cuota",
            headerName: getLocalizedString("quotaValue"),
            isObligatory: true,
            maxLength: 254,
            minLength: 0,
            isNumeric: true,
            dataType: "number",
            validationUnrequired: true,
        });

        columns.push({
            headerName: getLocalizedString("reference"),
            field: "Referencia",
            isObligatory: false,
            maxLength: 254,
            minLength: 0,
            disabled: true,
        });

        if (entryStructure.periodicity === "C") {
            columns.push({
                field: "Período de descuento",
                headerName: getLocalizedString("periodDiscount"),
                isObligatory: true,
                maxLength: 254,
                minLength: 0,
                disabled: true,
            });
        } else {
            columns.push({
                field: "Concepto condicional 1",
                headerName: getLocalizedString("conditionalConcept1"),
                isObligatory: false,
                isSelect: true,
            });

            columns.push({
                field: "Concepto condicional 2",
                headerName: getLocalizedString("conditionalConcept2"),
                isObligatory: false,
                isSelect: true,
            });

            columns.push({
                field: "Concepto condicional 3",
                headerName: getLocalizedString("conditionalConcept3"),
                isObligatory: false,
                isSelect: true,
            });

            columns.push({
                field: "Condicional cuota",
                headerName: getLocalizedString("conditionalInstallment"),
                isObligatory: false,
                isSelect: true,
            });
        }
        return columns;
    }, [getEntryStructure]);

    const handleValidateAndExecute = () => {
        handleExecute([formData["Código de empleado"]]);
    };

    const handleValidateAndViewResult = () => {
        handleViewResult({ "Código de empleado": formData["Código de empleado"] }, formData["Código de empleado"]);
    };

    return (
        <>
            <Grid container item xs={12} direction="row" style={{ paddingTop: 10 }}>
                <Grid container item xs={12}>
                    <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                        {" "}
                        {/* codigo */}
                        <TextField
                            disabled={true}
                            required
                            fullWidth
                            margin={"none"}
                            onChange={(e) => setFormValue(e.target.value, "Código de empleado")}
                            value={formData["Código de empleado"]}
                            label={getLocalizedString("code")}
                            inputProps={{ maxLength: 8 }}
                        />
                    </Grid>
                    <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                        {" "}
                        {/* nombre */}
                        <TextField
                            disabled={true}
                            required
                            fullWidth
                            margin={"none"}
                            value={formData["Apellido y nombres"]}
                            onChange={(e) => setFormValue(e.target.value, "Apellido y nombres")}
                            label={getLocalizedString("nameAndSurname")}
                            inputProps={{ maxLength: 60 }}
                        />
                    </Grid>
                    <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                        {" "}
                        {/* Fecha de ejecucion específica */}
                        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                            <DatePicker
                                fullWidth
                                disabled={!isCreate}
                                label={getLocalizedString("loanDate")}
                                cancelLabel={getLocalizedString("cancel")}
                                okLabel={getLocalizedString("ok")}
                                invalidDateMessage=""
                                format="dd/MM/yyyy"
                                required
                                margin={"none"}
                                value={formData["Fecha del préstamo"]}
                                onChange={(e) => setLoanDate(e)}
                            />
                        </MuiPickersUtilsProvider>
                    </Grid>
                </Grid>
                <Grid container item xs={12}>
                    <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                        <FormControl style={{ width: "100%" }}>
                            <InputLabel required id={`label-concepto`}>
                                {getLocalizedString("employeesProcessConcepto")}
                            </InputLabel>
                            <Select
                                disabled={!isCreate}
                                required
                                value={formData["Concepto"]}
                                labelId={`label-concepto`}
                                id={`select-concepto`}
                                onChange={(e) =>
                                    setConcept(e.target.value, selects.conceptsAssociatedWithEntryStructure)
                                }
                                margin={"none"}
                            >
                                {selects &&
                                    selects.conceptsAssociatedWithEntryStructure &&
                                    selects.conceptsAssociatedWithEntryStructure.map((item) => (
                                        <MenuItem className={"whiteText"} key={item.key} value={item.key}>
                                            {item.value}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                        {" "}
                        {/* nombre */}
                        <TextField
                            fullWidth
                            disabled={!isCreate}
                            required
                            margin={"none"}
                            value={formData["Nombre del concepto"]}
                            onChange={(e) => setFormValue(e.target.value, "Nombre del concepto")}
                            label={getLocalizedString("employeesProcessNombreConcepto")}
                        />
                    </Grid>
                    <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                        <FormControl style={{ width: "100%" }}>
                            <InputLabel required id={`label-moneda`}>
                                {getLocalizedString("coin")}
                            </InputLabel>
                            <Select
                                disabled={!isCreate}
                                required
                                value={formData["Moneda"]}
                                onChange={(e) => setFormValue(e.target.value, "Moneda")}
                                margin={"none"}
                                labelId={`label-moneda`}
                                id={`select-moneda`}
                            >
                                {selects &&
                                    selects.codeRefs &&
                                    selects.codeRefs.map((item) => (
                                        <MenuItem className={"whiteText"} key={item.key} value={item.key}>
                                            {item.value}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Grid container item xs={12}>
                    <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                        {" "}
                        {/* nombre */}
                        <TextField
                            fullWidth
                            disabled={!isCreate}
                            required
                            margin={"none"}
                            value={formData["Total del préstamo"]}
                            onChange={(e) => setFormValue(e.target.value, "Total del préstamo")}
                            label={getLocalizedString("loanTotal")}
                            InputProps={{ inputComponent: NumberFormatCustom }}
                            inputProps={{ decimals: 2 }}
                        />
                    </Grid>
                    <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                        {" "}
                        {/* nombre */}
                        <TextField
                            fullWidth
                            InputProps={{ inputComponent: CustomNumberFormat }}
                            disabled={!isCreate}
                            margin={"none"}
                            required
                            value={formData["Cantidad de cuotas"]}
                            onChange={(e) => setFormValue(e.target.value, "Cantidad de cuotas")}
                            label={getLocalizedString("installmentsCount")}
                            inputProps={{ min: "1", step: "1" }}
                        />
                    </Grid>
                    <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                        {" "}
                        {/* nombre */}
                        <FormControl style={{ width: "100%" }}>
                            <InputLabel required id={`label-frecuencia-descuento`}>
                                {getLocalizedString("discountFrequency")}
                            </InputLabel>
                            <Select
                                disabled
                                required
                                value={formData["Frecuencia del descuento"]}
                                onChange={(e) => setFormValue(e.target.value, "Frecuencia del descuento")}
                                margin={"none"}
                                labelId={`label-frecuencia-descuento`}
                                id={`select-frecuencia-descuento`}
                            >
                                {selects &&
                                    selects.periodicity &&
                                    selects.periodicity.map((item) => (
                                        <MenuItem className={"whiteText"} key={item.key} value={item.key}>
                                            {item.value}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                            {" "}
                            {/* nombre */}
                            <TextField
                                fullWidth
                                disabled={true}
                                margin={"none"}
                                required
                                value={formData["Valor pagado"]}
                                onChange={(e) => setFormValue(e.target.value, "Valor pagado")}
                                label={getLocalizedString("paidValue")}
                                InputProps={{ inputComponent: NumberFormatCustom }}
                                inputProps={{ decimals: 2 }}
                            />
                        </Grid>
                        <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                            {" "}
                            {/* nombre */}
                            <TextField
                                fullWidth
                                disabled={true}
                                required
                                InputProps={{ inputComponent: CustomNumberFormat }}
                                margin={"none"}
                                value={formData["Cuotas pagadas"]}
                                onChange={(e) => setFormValue(e.target.value, "Cuotas pagadas")}
                                label={getLocalizedString("paidInstallments")}
                                inputProps={{ min: "0", step: "1" }}
                            />
                        </Grid>
                        {getEntryStructure().periodicity === "C" && (
                            <Grid container item alignItems="center" xs={4} style={gridStyle}>
                                {" "}
                                {/* Fecha de ejecucion específica */}
                                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                                    <DatePicker
                                        fullWidth
                                        disabled={!isCreate}
                                        required
                                        label={getLocalizedString("discountStartPeriod")}
                                        cancelLabel={getLocalizedString("cancel")}
                                        okLabel={getLocalizedString("ok")}
                                        invalidDateMessage=""
                                        format="yyyy/MM"
                                        margin={"none"}
                                        value={formData["Período de inicio del descuento"]}
                                        views={["month", "year"]}
                                        onChange={(e) => modifyDataInDataGrid(e, "Período de inicio del descuento")}
                                    />
                                </MuiPickersUtilsProvider>
                            </Grid>
                        )}
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                            {" "}
                            {/* nombre */}
                            <TextField
                                fullWidth
                                disabled={true}
                                required
                                margin={"none"}
                                value={formData["Saldo pendiente"]}
                                label={getLocalizedString("pendingBalance")}
                                InputProps={{ inputComponent: NumberFormatCustom }}
                                inputProps={{ decimals: 2 }}
                                onChange={(e) => setFormValue(e.target.value, "Saldo pendiente")}
                            />
                        </Grid>
                        <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                            {" "}
                            {/* nombre */}
                            <TextField
                                fullWidth
                                disabled={true}
                                required
                                InputProps={{ inputComponent: CustomNumberFormat }}
                                margin={"none"}
                                value={formData["Cuotas pendientes"]}
                                onChange={(e) => setFormValue(e.target.value, "Cuotas pendientes")}
                                label={getLocalizedString("pendingQuotas")}
                                inputProps={{ min: "0", step: "1" }}
                            />
                        </Grid>
                        <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                            {" "}
                            {/* nombre */}
                            <TextField
                                fullWidth
                                required
                                InputProps={{ inputComponent: CustomNumberFormat }}
                                margin={"none"}
                                value={formData["Número de cuota de referencia"]}
                                label={getLocalizedString("referenceQuotaNumber")}
                                onChange={(e) => modifyDataInDataGrid(e.target.value, "Número de cuota de referencia")}
                                inputProps={{ min: "0", step: "1" }}
                            />
                        </Grid>
                    </Grid>
                    {getEntryStructure().thirdPartyPayment && (
                        <>
                            {" "}
                            <Grid container item xs={12}>
                                <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                                    {" "}
                                    {/* nombre */}
                                    <FormControl style={{ width: "100%" }}>
                                        <InputLabel required id={`label-tercero`}>
                                            {getLocalizedString("thirdPartySingular")}
                                        </InputLabel>
                                        <Select
                                            disabled={!isCreate}
                                            required
                                            value={formData["Tercero"]}
                                            onChange={(e) => setFormValue(e.target.value, "Tercero")}
                                            margin={"none"}
                                            labelId={`label-tercero`}
                                            id={`select-tercero`}
                                        >
                                            {selects &&
                                                selects.thirdParties &&
                                                selects.thirdParties.map((item) => (
                                                    <MenuItem className={"whiteText"} key={item.key} value={item.key}>
                                                        {item.value}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                                    {" "}
                                    {/* nombre */}
                                    <FormControl style={{ width: "100%" }}>
                                        <InputLabel required id={`label-forma-de-pago`}>
                                            {getLocalizedString("paymentMethod")}
                                        </InputLabel>
                                        <Select
                                            disabled={!isCreate}
                                            required
                                            value={formData["Forma de pago"]}
                                            onChange={(e) => setPaymenthMethodValue(e.target.value)}
                                            margin={"none"}
                                            labelId={`label-forma-de-pago`}
                                            id={`select-forma-de-pago`}
                                        >
                                            {selects &&
                                                selects.paymentMethods &&
                                                selects.paymentMethods.map((item) => (
                                                    <MenuItem className={"whiteText"} key={item.key} value={item.key}>
                                                        {item.value}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                                    {" "}
                                    {/* nombre */}
                                    <FormControl style={{ width: "100%" }}>
                                        <InputLabel required id={`label-tipo-de-pago`}>
                                            {getLocalizedString("paymentType")}
                                        </InputLabel>
                                        <Select
                                            disabled={!isCreate}
                                            required
                                            value={formData["Tipo de pago"]}
                                            onChange={(e) => setFormValue(e.target.value, "Tipo de pago")}
                                            margin={"none"}
                                            labelId={`label-tipo-de-pago`}
                                            id={`select-tipo-de-pago`}
                                        >
                                            {selects &&
                                                selects.paymentTypes &&
                                                selects.paymentTypes.map((item) => (
                                                    <MenuItem className={"whiteText"} key={item.key} value={item.key}>
                                                        {item.value}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}>
                                <Grid container item alignItems="center" xs={12} sm={4} style={gridStyle}>
                                    {" "}
                                    {/* nombre */}
                                    <FormControl style={{ width: "100%" }}>
                                        <InputLabel required={formData["Forma de pago"] === "D"} id={`label-banco`}>
                                            {getLocalizedString("bank")}
                                        </InputLabel>
                                        <Select
                                            disabled={!isCreate || (isCreate && formData["Forma de pago"] !== "D")}
                                            value={formData["Banco"]}
                                            onChange={(e) => setFormValue(e.target.value, "Banco")}
                                            margin={"none"}
                                            labelId={`label-banco`}
                                            id={`select-banco`}
                                        >
                                            {selects &&
                                                selects.banks &&
                                                selects.banks.map((item) => (
                                                    <MenuItem className={"whiteText"} key={item.key} value={item.key}>
                                                        {item.value}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid container item alignItems="center" xs={12} sm={8} style={gridStyle}>
                                    {" "}
                                    {/* nombre */}
                                    <TextField
                                        disabled={!isCreate || (isCreate && formData["Forma de pago"] !== "D")}
                                        required={formData["Forma de pago"] === "D"}
                                        fullWidth
                                        margin={"none"}
                                        value={formData["Cuenta bancaria"]}
                                        onChange={(e) => setFormValue(e.target.value, "Cuenta bancaria")}
                                        label={getLocalizedString("bankAccount")}
                                        inputProps={{ maxLength: 60 }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}>
                                <Grid container item alignItems="center" xs={12} style={gridStyle}>
                                    {" "}
                                    {/* nombre */}
                                    <TextField
                                        disabled={!isCreate}
                                        fullWidth
                                        margin={"none"}
                                        value={formData["Comentarios del pago al tercero"]}
                                        onChange={(e) =>
                                            setFormValue(e.target.value, "Comentarios del pago al tercero")
                                        }
                                        label={getLocalizedString("paymentToThirdPartyComments")}
                                        inputProps={{ maxLength: 60 }}
                                        multiline={true}
                                        rows={1}
                                        rowsMax={3}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                    <Grid container item justify="flex-end" alignItems="center" xs={12} style={{ marginTop: 10 }}>
                        {!isCreate && (
                            <div>
                                <ButtonDialogAction
                                    hasMarginRight
                                    isAccept
                                    onClick={openPrePaymentDialog}
                                    text={getLocalizedString("prepay")}
                                />
                            </div>
                        )}
                        {!isCreate && getEntryStructure().periodicity === "C" && (
                            <div style={{ marginLeft: 10 }}>
                                <ButtonDialogAction
                                    isAccept
                                    onClick={showConfirmPostponeDialog}
                                    text={getLocalizedString("postpone")}
                                />
                            </div>
                        )}
                        {isCreate && (
                            <div style={{ marginLeft: 10 }}>
                                <ButtonDialogAction
                                    isAccept
                                    onClick={() => calculateGridData(gridRef)}
                                    text={getLocalizedString("generateInstallments")}
                                />
                            </div>
                        )}
                    </Grid>
                    <Grid container item xs={12}>
                        <EditableGridv2
                            nestedGrid
                            batchMode={true}
                            selectDataSources={selectDataSources}
                            setShouldReloadGrid={setShouldReloadGrid}
                            shouldReloadGrid={shouldReloadGrid}
                            ref={gridRef}
                            gridName={"entryByInstallments"}
                            rowData={gridData}
                            columns={columnsData}
                            getColaboratorData={getColaboratorData}
                        />
                    </Grid>
                </Grid>
                {!isCreate && automaticProcessesIsEnabled && (
                    <Grid container item xs={12} justify={"flex-end"} alignItems="center" style={{ paddingBottom: 10 }}>
                        <Grid item style={{ paddingRight: 10 }}>
                            <CustomIconButton
                                title={
                                    processList.length === 1
                                        ? `${getLocalizedString("calculateProcess")}: ${processList[0].value}`
                                        : getLocalizedString("calculateProcess")
                                }
                                onClick={handleValidateAndExecute}
                                type={"execute"}
                            />
                        </Grid>
                        <Grid item>
                            <CustomIconButton
                                title={getLocalizedString("viewResult")}
                                onClick={handleValidateAndViewResult}
                            >
                                <VisibilityIcon />
                            </CustomIconButton>
                        </Grid>
                    </Grid>
                )}
                <Grid container item xs={12} justify={"flex-end"} alignItems="center" style={{ paddingBottom: 20 }}>
                    <ButtonDialogAction isAccept onClick={submit} text={getLocalizedString("save")} />
                </Grid>
            </Grid>
            {confirmPrepaymentDialogIsOpen && (
                <ConfirmPrepaymentDialog
                    handleConfirm={prepayInstallments}
                    open={confirmPrepaymentDialogIsOpen}
                    availableInstallments={unpaidInstallmentsDialog}
                    handleClose={() => setConfirmPrepaymentDialogIsOpen(false)}
                />
            )}
            {processSelectionDialogIsOpen && (
                <SelectProcessDialog
                    open={processSelectionDialogIsOpen}
                    title={
                        isExecution
                            ? getLocalizedString("selectProcessToRun")
                            : getLocalizedString("selectProcessToQuery")
                    }
                    processList={processList}
                    handleClose={handleCloseProcessDialogSelection}
                    handleExecute={isExecution ? executeProcess : viewResult}
                />
            )}
            {resultDialogIsOpen && !(isLoading || automaticProcessesIsLoading) && (
                <ResultGridDialog
                    open={resultDialogIsOpen}
                    templates={resultData.templates}
                    process={resultData.process}
                    employeeName={formData["Apellido y nombres"]}
                    handleClose={handleCloseResultDialog}
                    handleOpenPickTemplateDialog={handleOpenPickTemplateDialog}
                    handleGenerateDocumentsGivenTemplate={handleGenerateDocumentsGivenTemplate}
                />
            )}
            {resultData?.templates?.length > 0 &&
                pickTemplateDialogIsOpen &&
                !(isLoading || automaticProcessesIsLoading) && (
                    <PickTemplateDialog
                        open={pickTemplateDialogIsOpen}
                        templates={resultData.templates}
                        handleConfirm={handleGenerateDocumentsGivenTemplate}
                        handleClose={handleClosePickTemplateDialog}
                    />
                )}
        </>
    );
};

export default Form;
